import {
  memo,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  InitialTableState,
  Row,
  SortingState,
  Table,
  TableState,
  useReactTable,
} from "@tanstack/react-table";
import { GraphQLError } from "graphql";
import { clamp, isEmpty, omit, toArray } from "lodash";
import { useLocation } from "react-router";
import {
  buildLocalTableInfo,
  useLocalTableDisplayConfig,
} from "../../../hooks/useTableDisplayConfig";
import { borderStyles, CSS } from "../../../stitches.config";
import { handleKeyDown } from "../../../utils/accessibility";
import { AuthContext } from "../../Authorization/AuthContext";
import { PUBLIC_WIDTH } from "../../Guest/__styles__/Layout";
import { FlexRow } from "../__styles__/Layout";
import { ACTION_COLUMN_DEF_CONSTANTS } from "../ActionCell";
import {
  DeprecatedFilters,
  Filter,
  FilterState,
} from "../DeprecatedTableFilters";
import { Icon } from "../Icons/LucideIcons";
import {
  ManualPaginationConfig,
  useLegacyTableStateInURL,
} from "../Tables/hooks";
import PaginationSection from "../Tables/PaginationSection";
import {
  DEFAULT_PAGE_SIZE,
  getCellMinWidth,
  LoadingDetails,
} from "../Tables/utils";
import {
  CellDiv,
  ColumnGradient,
  LoadingOverlay,
  NoDataOverlay,
  Resizer,
  StyledTable,
  TableFooter,
  TableHeader,
} from "./__styles__/FullWidthTable";
import { DropdownMenu } from "./DropdownMenu";
import { ScrollContainer, ScrollContainerContext } from "./ScrollContainer";
import { TableContext } from "./TableContext";
import { TimeoutErrorState } from "./TimeoutErrorState";
import { Attribute } from "./types";
import { sanityCheckColumnSizing, sanityCheckSorting } from "./utils";

export interface DeprecatedFullWidthTableProps<T> {
  previousData?: Array<T>;
  currentData: Array<T>;
  columns: Array<ColumnDef<T>>;
  initialState?: InitialTableState & { filters?: FilterState };
  loadingDetails: LoadingDetails;
  manualPaginationConfig?: ManualPaginationConfig;
  actions?: ReactNode;
  prevLocation?: string;
  filterable?: {
    filterConfigurations?: Array<Filter>;
    search: (params: {
      filters: FilterState;
      sort: SortingState;
      page: number;
      columns: Array<ColumnDef<T>>;
    }) => void;
  };
  interactiveHeaders?: boolean;
  rowCanExpand?: (row: Row<T>) => boolean;
  excludePaginationNav?: boolean;
  excludeTableHeader?: boolean;
  setTableStateInURL?: ReturnType<typeof useLegacyTableStateInURL>[1];
  withoutSideNav?: boolean;
  columnSettingProps?: Maybe<{
    columnConfiguration: Attribute[];
    columnDefinitions: ColumnDef<T>[];
  }>;
  timeoutError?: Maybe<GraphQLError>;
}

export const DeprecatedFullWidthTable = <T,>({
  previousData,
  currentData,
  columns: initialColumns,
  initialState = {
    pagination: {
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
  },
  loadingDetails,
  manualPaginationConfig,
  prevLocation,
  actions,
  filterable,
  interactiveHeaders = true,
  rowCanExpand = () => false,
  excludePaginationNav = false,
  setTableStateInURL,
  withoutSideNav = false,
  columnSettingProps,
  timeoutError,
}: DeprecatedFullWidthTableProps<T> & { css?: CSS }) => {
  const { user, admin, isPublic } = useContext(AuthContext);
  const { pathname } = useLocation();
  const { loading, loadingText, noDataText } = loadingDetails;
  const [localLoading, setLocalLoading] = useState(loading);
  const [scrollContainerHeight, setScrollContainerHeight] = useState("100%");
  const tableRef = useRef(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [filterState, setFilterState] = useState<FilterState>(
    initialState.filters ?? {},
  );
  const [columnOrder, setColumnOrder] = useState<Array<string>>(() =>
    initialColumns.map(c => c.id!),
  );
  const [columns, setColumns] = useState(initialColumns);

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const { id: tableId, name: tableName } = buildLocalTableInfo({
    entityId: user?.id ?? admin?.id,
    pathname,
  });

  const {
    getLocalTableState,
    setLocalColumnOrder,
    setLocalColumnSizing,
    setLocalSorting,
  } = useLocalTableDisplayConfig({
    tableId,
    defaultValue: {
      columnOrder: initialColumns.map(column => column.id!),
      columnSizing: {},
      sorting: [],
    },
  });

  const localTableConfig = getLocalTableState();

  const { columnSizing, sorting } = localTableConfig;

  const newColumnSizing = sanityCheckColumnSizing({
    columnSizing,
    columns,
  });

  const newSorting = sanityCheckSorting({ sorting, columns });

  const data = localLoading || loading ? (previousData ?? []) : currentData;

  const tableState: Partial<TableState> = {
    columnOrder,
  };

  if (manualPaginationConfig) {
    tableState.pagination = manualPaginationConfig.pagination;
  }

  const defaultColumn = {
    size: 150,
    maxSize: 400,
    minSize: 100,
  };

  const onColumnChange = (columnId: string, action: "add" | "remove") => {
    let newList = [...columns];

    if (action === "add") {
      const definition = columnSettingProps?.columnDefinitions.find(
        c => c.id === columnId,
      );

      if (definition) {
        newList.splice(-1, 0, definition);
        setColumns(newList);
        setColumnOrder(newList.map(c => c.id!));
      }
    } else {
      newList = columns.filter(column => column.id !== columnId);
      setColumns(newList);
      setColumnOrder(newList.map(c => c.id!));
    }
  };

  const table = useReactTable({
    data,
    columns,
    initialState: {
      columnSizing: newColumnSizing ?? columnSizing,
      sorting: newSorting ?? sorting,
      ...omit(initialState, "filters"),
      ...(excludePaginationNav ?
        {
          pagination: {
            pageIndex: 0,
            pageSize:
              DEFAULT_PAGE_SIZE > data.length ? DEFAULT_PAGE_SIZE : data.length,
          },
        }
      : {}),
    },
    state: tableState,
    manualSorting: false,
    getSortedRowModel: getSortedRowModel(),
    ...(manualPaginationConfig ?
      omit(manualPaginationConfig, "pagination")
    : {
        manualPagination: false,
        getPaginationRowModel: getPaginationRowModel(),
      }),
    onColumnOrderChange: setColumnOrder,
    autoResetPageIndex: false,
    enableExpanding: true,
    getRowCanExpand: rowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    columnResizeMode: "onChange",
    defaultColumn,
  });

  const innerTableState = table.getState();

  const search = () =>
    filterable?.search({
      filters: filterState,
      sort: innerTableState.sorting,
      page: innerTableState.pagination.pageIndex + 1,
      columns,
    });

  useEffect(() => {
    if (setTableStateInURL) {
      setTableStateInURL({
        filters: filterState,
        pagination: innerTableState.pagination,
        sorting: innerTableState.sorting,
        prevLocation,
      });

      search();
    }
    // Adding proper dependencies to the search function would causes infinite re-renders on the RepLoss table
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterState,
    innerTableState.pagination,
    innerTableState.sorting,
    columns,
  ]);

  useEffect(() => {
    if (!setTableStateInURL) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerTableState.pagination]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLocalLoading(false);
    }, 250);
    return () => clearTimeout(timeout);
  }, [loading]);

  useEffect(() => {
    if (!scrollableRef.current) return;

    const siblings = toArray(
      scrollableRef.current.parentElement?.children,
    ).filter(child => {
      const ignoredClassNames = ["scroll-wrapper", "loading-overlay"];
      return !toArray(child.classList).some(className =>
        ignoredClassNames.includes(className),
      );
    });

    const combinedHeightOfSiblings = siblings.reduce((totalHeight, element) => {
      return totalHeight + element.clientHeight;
    }, 0);

    setScrollContainerHeight(`calc(100vh - ${combinedHeightOfSiblings}px)`);
  }, [scrollableRef.current?.parentElement?.children, loading]);

  useEffect(() => {
    setLocalColumnSizing(innerTableState.columnSizing);
  }, [setLocalColumnSizing, innerTableState.columnSizing]);

  useEffect(() => {
    table.setPagination(prevPagination => ({
      ...prevPagination,
      pageIndex: 0,
    }));
    setLocalSorting(innerTableState.sorting);
    // Adding setLocalSorting to the dependencies would cause infinite re-renders on all new tables and old tables
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerTableState.sorting, table]);

  const legacyFilterChange = (filters: Record<string, unknown>) => {
    table.setPagination(prevPagination => ({
      ...prevPagination,
      pageIndex: 0,
    }));

    setFilterState(filters);
  };

  const Table = () => {
    const { scrolledToLeft, scrolledToRight, handleScroll } = useContext(
      ScrollContainerContext,
    );

    const showBottomBorder = data.length < DEFAULT_PAGE_SIZE;

    const Gradient = ({ index }: { index: number }) => {
      const getGradientDirection = (index: number) => {
        if (index === 0 && !scrolledToLeft) {
          return "leftToRight";
        } else if (index === columns.length - 1 && !scrolledToRight) {
          return "rightToLeft";
        } else {
          return "none";
        }
      };

      return (
        <ColumnGradient
          direction={getGradientDirection(index)}
          className="gradient"
        />
      );
    };

    useEffect(() => {
      setLocalColumnOrder(columnOrder);
      // A better fix here is to top creating nested components and resolve this then. I will get to that later in this series.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnOrder]);

    const columnSizeVars = useMemo(() => {
      const headers = table.getFlatHeaders();
      const colSizes: { [key: string]: number | string } = {};

      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]!;

        //We keep the last column at 100%, since it needs to take up all remaining available space in table width
        const isLastColumn = i === headers.length - 1;
        if (isLastColumn) {
          colSizes[header.id] = isPublic ? "100px" : "100%";
        } else {
          const savedSize = columnSizing[header.id];
          const defaultSize =
            savedSize ? savedSize : (
              (header.column.columnDef.size ?? defaultColumn.size)
            );
          const newSize =
            innerTableState.columnSizing[header.id] ?? defaultSize;

          const min = header.column.columnDef.minSize ?? defaultColumn.minSize;
          const max = header.column.columnDef.maxSize ?? defaultColumn.maxSize;

          const size = clamp(newSize, min, max);

          colSizes[header.id] = size;
        }
      }

      return colSizes;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [innerTableState.columnSizingInfo, innerTableState.columnSizing]);

    useLayoutEffect(() => {
      handleScroll();
    }, [columnSizeVars, handleScroll]);

    const TableBody = ({ table }: { table: Table<T> }) => {
      return (
        <tbody>
          {table.getRowModel().rows.map(row => {
            return (
              <tr key={row.id} data-testid="row">
                {row.getVisibleCells().map((cell, index) => {
                  const value = cell.getValue();
                  const hoverText = typeof value === "string" ? value : "";
                  return (
                    <td
                      key={cell.id}
                      style={{
                        width: columnSizeVars[cell.column.id],
                        minWidth: getCellMinWidth<T>(cell),
                      }}
                    >
                      <CellDiv
                        isActionColumn={
                          cell.column.id === ACTION_COLUMN_DEF_CONSTANTS.id
                        }
                        title={hoverText}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </CellDiv>
                      <Gradient index={index} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      );
    };

    const MemoizedTableBody = memo<typeof TableBody>(
      TableBody,
      (prev, next) => prev.table.options.data === next.table.options.data,
    );

    return (
      <StyledTable
        isPublic={isPublic}
        ref={tableRef}
        style={{
          borderBottom: showBottomBorder ? `${borderStyles.solid}` : "unset",
          width: isPublic ? PUBLIC_WIDTH : table.getTotalSize(),
          ...columnSizeVars,
        }}
      >
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index, array) => {
                const isLastColumn = index === array.length - 1;
                const orderableClass =
                  interactiveHeaders && !isLastColumn ? "order-pointer" : "";
                const sortableClass =
                  header.column.getCanSort() ? "sort-pointer" : "";
                const sortDirectionClass =
                  {
                    asc: "sort-asc",
                    desc: "sort-desc",
                  }[header.column.getIsSorted() as string] ?? "";

                const { tooltip, className: additionalClasses = "" } =
                  header.column.columnDef.meta ?? {};

                const columnHeader = header.column.columnDef.header;
                const hoverText =
                  tooltip ? tooltip
                  : typeof columnHeader === "string" ? columnHeader
                  : "";

                return (
                  <th
                    key={header.id}
                    role="columnheader"
                    className={`${orderableClass} ${sortableClass} ${sortDirectionClass} ${additionalClasses}`}
                    style={{
                      width: columnSizeVars[header.id],
                      minWidth: getCellMinWidth<T>(header),
                    }}
                    onKeyPress={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        interactiveHeaders &&
                          setOpenDropdownId(
                            openDropdownId === header.id ? null : header.id,
                          );
                      }
                    }}
                    onClick={() => {
                      interactiveHeaders &&
                        setOpenDropdownId(
                          openDropdownId === header.id ? null : header.id,
                        );
                    }}
                    tabIndex={
                      header.column.getCanSort() || interactiveHeaders ?
                        0
                      : undefined
                    }
                    data-testid={`header-${header.id}`}
                    data-tooltip={hoverText}
                  >
                    <div>
                      {tooltip && (
                        <Icon
                          iconName="info"
                          size={12}
                          color={"contentSecondary"}
                          className="info-icon"
                          style={{
                            marginRight: "8px",
                            top: "1px",
                            position: "relative",
                          }}
                        />
                      )}
                      {header.isPlaceholder ? null : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </div>
                    {/* we don't include the resizer on the last column since it needs to take up the rest of the available table space */}
                    {!isLastColumn && (
                      <Resizer
                        key={header.id}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => header.column.resetSize()}
                        className={`resizer`}
                        isResizing={header.column.getIsResizing()}
                        data-testid={`resizer-${header.id}`}
                      />
                    )}
                    <Gradient index={index} />
                    <DropdownMenu
                      header={header}
                      setColumnOrder={setColumnOrder}
                      open={openDropdownId === header.id && !isLastColumn}
                      setOpen={isOpen =>
                        setOpenDropdownId(isOpen ? header.id : null)
                      }
                      removeColumn={id => onColumnChange(id, "remove")}
                    />
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <MemoizedTableBody table={table} />
      </StyledTable>
    );
  };

  return (
    <>
      <TableContext.Provider
        value={{
          name: tableName,
        }}
      >
        <TableHeader isPublic={isPublic}>
          <FlexRow gap="s">
            {filterable && (
              <DeprecatedFilters
                filterConfigurations={filterable.filterConfigurations!}
                filterValues={filterState}
                onFilterChange={legacyFilterChange}
                handleKeyDown={handleKeyDown}
              />
            )}
          </FlexRow>
          <FlexRow gap="s">{actions}</FlexRow>
        </TableHeader>
        <ScrollContainer
          scrollableRef={scrollableRef}
          height={scrollContainerHeight}
          loading={localLoading || loading}
          withoutSideNav={withoutSideNav}
        >
          <Table />
          {!localLoading &&
            !loading &&
            !timeoutError &&
            isEmpty(currentData) && <NoDataOverlay>{noDataText}</NoDataOverlay>}
          {(localLoading || loading) && (
            <LoadingOverlay className="loading-overlay">
              {loadingText}
            </LoadingOverlay>
          )}
          {timeoutError && (
            <TimeoutErrorState
              refetch={() =>
                filterable?.search({
                  filters: filterState,
                  sort: innerTableState.sorting,
                  page: innerTableState.pagination.pageIndex + 1,
                  columns,
                })
              }
            />
          )}

          <TableFooter>
            {!loading && !excludePaginationNav && (
              <PaginationSection
                paginationDetails={{
                  pageCount: table.getPageCount(),
                  pagination: innerTableState.pagination,
                  setPagination: table.setPagination,
                }}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
                canGetPreviousPage={table.getCanPreviousPage}
                canGetNextPage={table.getCanNextPage}
              />
            )}
          </TableFooter>
        </ScrollContainer>
      </TableContext.Provider>
    </>
  );
};
